import { PDFDocument } from 'pdf-lib';

export type LeaseFillData = {
  tenantName: string;
  email: string;
  phone: string;
  apartmentNumber: string;
  startDateISO: string;
  monthlyRent: number;
  noticePeriod: 1 | 3;
  isCommercial?: boolean;
  tenantAddress?: {
    number?: string;
    street?: string;
    neighborhood?: string;
    municipality?: string;
    city?: string;
  };
  propertyCode?: string;
  propertyContact?: string;
  templateUrl?: string;
};

export async function fillLeasePdf(data: LeaseFillData): Promise<{ blobUrl: string; blob: Blob }> {
  try {
    // Choose template based on property type
    let templateUrl = data.templateUrl;
    if (!templateUrl) {
      if (data.isCommercial) {
        templateUrl = '/templates/Contrat de location commercial MEC 2025.pdf';
      } else {
        templateUrl = '/templates/Contrat de location residentiel MEC 2025.pdf';
      }
    }

    const response = await fetch(templateUrl);
    if (!response.ok) {
      throw new Error(`Failed to load PDF template: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/pdf')) {
      throw new Error(`Invalid file type. Expected PDF but got: ${contentType}`);
    }

    const existingPdfBytes = await response.arrayBuffer();

    if (existingPdfBytes.byteLength === 0) {
      throw new Error('PDF template is empty');
    }

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const form = pdfDoc.getForm();

    // Format start date as day/month/year (e.g., "15/01/2025")
    const startDate = new Date(data.startDateISO);
    const formattedStartDate = `${String(startDate.getDate()).padStart(2, '0')}/${String(startDate.getMonth() + 1).padStart(2, '0')}/${startDate.getFullYear()}`;

    // Format current date as day/month/year
    const currentDate = new Date();
    const formattedCurrentDate = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${currentDate.getFullYear()}`;

    const setIfExists = (fieldName: string, value: string) => {
      try {
        const f = form.getTextField(fieldName);
        f.setText(value ?? '');
      } catch {
        // Field doesn't exist, skip silently
      }
    };

    // Fields for both residential and commercial templates
    if (data.isCommercial) {
      // Commercial lease specific fields - using exact field names from PDF
      setIfExists('full name', data.tenantName);

      // Address fields for commercial tenant
      if (data.tenantAddress) {
        setIfExists('AddressNumber', data.tenantAddress.number || '');
        setIfExists('address_street', data.tenantAddress.street || '');
        setIfExists('AddressNeighborhood', data.tenantAddress.neighborhood || '');
        setIfExists('AddressMunicipality', data.tenantAddress.municipality || '');
        setIfExists('AddressCity', data.tenantAddress.city || '');
      }

      setIfExists('Email', data.email);
      setIfExists('MobileNumber', data.phone);

      setIfExists('unitNumber', data.apartmentNumber);

      setIfExists('start date', formattedStartDate);

      setIfExists('current date', formattedCurrentDate);

      setIfExists('monthly rent', `${data.monthlyRent} USD`);

      // Notice period field
      setIfExists('notice period', `${data.noticePeriod}`);

    } else {
      // Residential lease fields (existing format)
      setIfExists('full name', data.tenantName);
      setIfExists('email', data.email);
      setIfExists('phone', data.phone);
      setIfExists('appartement number', data.apartmentNumber);
      setIfExists('start date', formattedStartDate);
      setIfExists('current date', formattedCurrentDate);
      setIfExists('monthly rent', `${data.monthlyRent} USD`);

      // Notice period field
      setIfExists('notice period', `${data.noticePeriod}`);
    }

    // Flatten the form to make it non-editable (static PDF)
    form.flatten();

    const pdfBytes = await pdfDoc.save();
    const uint8 = new Uint8Array(pdfBytes);
    const blob = new Blob([uint8.buffer], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    return { blobUrl, blob };
  } catch (error) {
    console.error('Error filling PDF:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while generating the PDF');
  }
}
